import React, { useState, useEffect, useCallback } from 'react';
import { openCertificate } from '@/lib/certificates';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate } from '@/lib/dateUtils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft, Calendar, Clock, MapPin, Briefcase, Users, CheckCircle2,
  Loader2, Video, ExternalLink, Link2, Award, Star, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { isWorkplaceMode, modeLabelFor, statusTone, experienceDisplay, hasStarted } from '@/lib/experienceStatus';
import { SessionAgenda, showAgendaFor } from '@/components/SessionAgenda';
import VisitDetailsCard from '@/components/VisitDetailsCard';

const SL = {
  scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled',
  requested: 'Requested', pending_parent_confirmation: 'Pending Confirmation',
  confirmed: 'Confirmed', awaiting_feedback: 'Awaiting Feedback',
};

const CONSENT_LABEL = { granted: 'Consent active', withdrawn: 'Consent withdrawn', pending: 'Consent pending' };
const CONSENT_TONE = {
  granted: 'bg-emerald-50 text-emerald-600',
  withdrawn: 'bg-red-50 text-red-600',
  pending: 'bg-amber-50 text-amber-700',
};

function TimelineStep({ label, done, active, last }) {
  return (
    <div className="flex items-start gap-3 mb-0" data-testid={`timeline-step-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex flex-col items-center">
        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${done ? 'bg-[#F97D6A] border-[#F97D6A]' : active ? 'border-[#F97D6A] bg-white' : 'border-gray-200 bg-white'}`}>
          {done && <CheckCircle2 className="h-2.5 w-2.5 text-white" />}
        </div>
        {!last && <div className={`w-0.5 h-5 ${done ? 'bg-[#F97D6A]' : 'bg-gray-200'}`} />}
      </div>
      <p className={`text-xs font-medium -mt-0.5 ${done ? 'text-[#1F2937]' : active ? 'text-[#F97D6A]' : 'text-[#B0B5C0]'}`}>{label}</p>
    </div>
  );
}

export default function GroupSessionDetailPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const pageWrapperClass = user?.role === 'parent' ? '' : 'py-6 sm:py-8 ce-section-bg min-h-[calc(100vh-64px)]';
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showComplete, setShowComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [cNotes, setCNotes] = useState('');
  const [cLearnings, setCLearnings] = useState('');
  const [cRemarks, setCRemarks] = useState('');
  const [attendance, setAttendance] = useState({});
  const [zipping, setZipping] = useState(false);
  const [reissue, setReissue] = useState(null);
  const [reissuing, setReissuing] = useState(false);

  const downloadAll = async () => {
    setZipping(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/certificates.zip`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificates-${sessionId}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No certificates are available to download yet');
    }
    setZipping(false);
  };

  const doReissue = async () => {
    setReissuing(true);
    try {
      await api.post(`/bookings/${reissue.booking_id}/regenerate-certificate`);
      toast.success('Certificate reissued');
      setReissue(null);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setReissuing(false);
  };

  const API_BASE = '';

  const fetchSession = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sessions/${sessionId}`);
      setSession(res.data);
      // Init attendance
      const att = {};
      (res.data.students || []).forEach(s => {
        att[s.booking_id] = { attended: s.attended ?? true, feedback: s.mentor_student_feedback || '' };
      });
      setAttendance(att);
      setCNotes(res.data.session_notes || '');
      setCLearnings(res.data.key_learnings || '');
      setCRemarks(res.data.mentor_remarks || '');
    } catch {
      toast.error('Failed to load session');
    }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const attList = Object.entries(attendance).map(([bid, a]) => ({
        booking_id: bid, attended: a.attended, feedback: a.feedback,
      }));
      await api.put(`/sessions/${sessionId}/complete`, {
        session_notes: cNotes || null,
        key_learnings: cLearnings || null,
        mentor_remarks: cRemarks || null,
        attendance: attList,
      });
      toast.success('Session completed — awaiting parent feedback');
      setShowComplete(false);
      fetchSession();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setCompleting(false);
  };

  if (loading) return (
    <div className={pageWrapperClass}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-8 w-32 bg-gray-200 rounded mb-6 animate-pulse" />
        <div className="ce-card p-8 animate-pulse"><div className="h-6 w-48 bg-gray-100 rounded mb-4" /><div className="h-4 w-72 bg-gray-100 rounded" /></div>
      </div>
    </div>
  );
  if (!session) return null;

  const s = session;
  const isPast = hasStarted(s);
  const studentStatuses = (s.students || []).map(st => st.status).filter(Boolean);
  const derivedStatus = s.status === 'scheduled' && studentStatuses.length
    && studentStatuses.every(x => x === 'pending_parent_confirmation')
    ? 'pending_parent_confirmation' : s.status;
  const displayStatus = experienceDisplay({ ...s, status: derivedStatus }).label;
  const canComplete = s.status === 'scheduled' && hasStarted(s) && (s.viewer_is_mentor || s.viewer_role === 'admin');
  const confirmedStudents = (s.students || []).filter(st => ['confirmed', 'awaiting_feedback', 'completed'].includes(st.status));
  const attendedStudents = (s.students || []).filter(st => st.attended === true);
  const allCertsGenerated = s.certificates_generated
    || ((s.students || []).some(st => st.attended === true)
        && (s.students || []).every(st => st.attended !== true || st.certificate_url));
  const isSchoolStaff = ['coordinator', 'admin'].includes(s.viewer_role);
  const certificateCount = (s.students || []).filter(st => st.certificate_url).length;

  // Timeline
  const steps = [
    { label: 'Requested', done: true },
    { label: 'Mentor Matched', done: !!s.mentor_name },
    { label: 'Parent Confirmed', done: confirmedStudents.length > 0 },
    { label: 'Session Completed', done: s.status === 'completed' },
    { label: 'Feedback Submitted', done: attendedStudents.some(st => st.feedback_rating) },
    { label: 'Certificates Generated', done: allCertsGenerated },
  ];
  const activeIdx = steps.findIndex(st => !st.done);

  return (
    <div className={pageWrapperClass} data-testid="group-session-detail">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-5 -ml-2 rounded-lg" data-testid="session-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>

        {/* Header */}
        <div className="ce-card-elevated p-5 sm:p-6 mb-5" data-testid="session-header">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <p className="text-xs font-bold text-[#B0B5C0] uppercase tracking-wider mb-1">Group Session</p>
              <h1 className="text-xl sm:text-2xl font-extrabold text-[#1F2937] font-['Outfit'] tracking-tight" data-testid="session-career">{s.career_name}</h1>
            </div>
            <Badge className={`text-[11px] ce-badge uppercase px-3 py-1 rounded-full border-0 ${statusTone({ ...s, status: derivedStatus })}`} data-testid="session-status">{displayStatus}</Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div className="flex items-center gap-2 text-[#6B7280]"><Calendar className="h-3.5 w-3.5 text-[#B0B5C0]" /><span>{formatDate(s.assigned_date) || 'Not scheduled'}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><Clock className="h-3.5 w-3.5 text-[#B0B5C0]" /><span data-testid="session-window">{s.assigned_time_slot || '-'}{s.assigned_end_time ? `–${s.assigned_end_time}` : ''}{s.assigned_duration_minutes ? ` (${s.assigned_duration_minutes}m)` : ''}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><MapPin className="h-3.5 w-3.5 text-[#B0B5C0]" /><span>{modeLabelFor(s.assigned_mode)}</span></div>
            <div className="flex items-center gap-2 text-[#6B7280]"><Users className="h-3.5 w-3.5 text-[#B0B5C0]" /><span>Students ({s.student_count})</span></div>
          </div>
          {!isWorkplaceMode(s.assigned_mode) && !['completed', 'cancelled', 'not_conducted'].includes(derivedStatus) && (
            <div className="mt-3">
              {s.google_meet_link ? (
                <a href={s.google_meet_link} target="_blank" rel="noopener noreferrer" data-testid="group-meet-link">
                  <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg h-8 text-xs px-4 font-semibold">
                    <Video className="h-3.5 w-3.5 mr-1.5" /> Join Google Meet
                  </Button>
                </a>
              ) : s.session_link ? (
                <a href={s.session_link} target="_blank" rel="noopener noreferrer" data-testid="group-meet-link">
                  <Button size="sm" className="bg-blue-600 text-white hover:bg-blue-700 rounded-lg h-8 text-xs px-4 font-semibold">
                    <Video className="h-3.5 w-3.5 mr-1.5" /> Join Session
                  </Button>
                </a>
              ) : (
                <p className="text-[11px] text-[#9CA3AF]" data-testid="group-meet-pending">
                  Google Meet link is being prepared — it will appear here and in your calendar invitation.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-5">
          <div className="sm:col-span-2 space-y-5">
            {!isWorkplaceMode(s.assigned_mode) && showAgendaFor(derivedStatus) && <SessionAgenda />}
            {isWorkplaceMode(s.assigned_mode) && (
              <VisitDetailsCard venue={s.assigned_venue} mentorName={s.mentor_name}
                                date={s.assigned_date} timeSlot={s.assigned_time_slot}
                                endTime={s.assigned_end_time} showWhen />
            )}
            {/* Students List */}
            <div className="ce-card p-5" data-testid="session-students">
              <div className="flex items-center justify-between gap-3 mb-3">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit']">
                  {s.viewer_sees_own_child_only ? 'Your child' : `Students (${s.student_count})`}
                </h2>
                {isSchoolStaff && (
                  <Button variant="outline" size="sm" onClick={downloadAll} disabled={!certificateCount || zipping}
                          className="rounded-xl h-8 text-xs border-gray-200 text-[#4B5563] font-medium"
                          data-testid="download-all-certificates-btn">
                    {zipping ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
                    Download all certificates{certificateCount ? ` (${certificateCount})` : ''}
                  </Button>
                )}
              </div>
              <div className="space-y-2">
                {(s.students || []).map((st) => (
                  <div key={st.booking_id} className="bg-[#F7F8FA] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`student-${st.booking_id}`}>
                    <div className="w-8 h-8 rounded-lg bg-[#FFF0ED] flex items-center justify-center text-sm font-bold text-[#F97D6A] shrink-0">{st.child_name?.charAt(0)}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1F2937]">{st.child_name}</p>
                      <p className="text-[10px] text-[#B0B5C0]">Grade {st.child_grade}{st.child_division ? `-${st.child_division}` : ''} · Parent: {st.parent_name}</p>
                      {st.mentor_student_feedback && (
                        <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed" data-testid={`student-note-${st.booking_id}`}>
                          <span className="text-[#B0B5C0]">Mentor&apos;s note:</span> {st.mentor_student_feedback}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                      {st.attended === false ? (
                        <Badge className="bg-red-50 text-red-600 border-0 text-[10px]" data-testid={`student-status-${st.booking_id}`}>
                          Closed · did not attend
                        </Badge>
                      ) : (
                        <Badge className={`border-0 text-[10px] ${statusTone(st)}`} data-testid={`student-status-${st.booking_id}`}>
                          {SL[st.status] || st.status}
                        </Badge>
                      )}
                      {isSchoolStaff && st.consent_status && (
                        <Badge className={`border-0 text-[10px] ${CONSENT_TONE[st.consent_status]}`} data-testid={`student-consent-${st.booking_id}`}>
                          {CONSENT_LABEL[st.consent_status]}
                        </Badge>
                      )}
                      {st.attended === true && <Badge className="bg-emerald-50 text-emerald-600 border-0 text-[10px]">Attended</Badge>}
                      {st.feedback_rating && (
                        <div className="flex items-center gap-0.5">
                          <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                          <span className="text-[10px] font-semibold text-[#1F2937]">{st.feedback_rating}</span>
                        </div>
                      )}
                      {st.certificate_url ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => openCertificate(st.certificate_url)}
                                  className="rounded-lg h-8 text-[11px] border-gray-200 text-[#4B5563] font-medium"
                                  data-testid={`download-certificate-${st.booking_id}`}>
                            <Award className="h-3.5 w-3.5 mr-1" /> Certificate
                          </Button>
                          {isSchoolStaff && (
                            <Button variant="ghost" size="sm" onClick={() => setReissue(st)}
                                    className="h-8 text-[11px] text-[#9CA3AF] hover:text-[#1F2937]"
                                    data-testid={`reissue-certificate-${st.booking_id}`}>
                              Reissue
                            </Button>
                          )}
                        </>
                      ) : (
                        <Badge className="bg-[#F1F2F4] text-[#9CA3AF] border-0 text-[10px]" data-testid={`certificate-state-${st.booking_id}`}>
                          {st.attended === true || st.status === 'awaiting_feedback' ? 'Certificate pending' : 'Certificate not available'}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mentor Info */}
            <div className="ce-card p-5" data-testid="session-mentor">
              <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Mentor</h2>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-base font-bold text-emerald-600">{s.mentor_name?.charAt(0)}</div>
                <div>
                  <p className="text-sm font-semibold text-[#1F2937]">{s.mentor_name}</p>
                  <p className="text-[10px] text-[#B0B5C0]">{s.career_name}</p>
                </div>
              </div>
            </div>

            {/* Session Notes */}
            {(s.session_notes || s.key_learnings || s.mentor_remarks) && (
              <div className="ce-card p-5" data-testid="session-notes">
                <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-3">Session Notes</h2>
                {s.session_notes && <div className="mb-3"><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Notes</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.session_notes}</p></div>}
                {s.key_learnings && <div className="mb-3"><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Key Learnings</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.key_learnings}</p></div>}
                {s.mentor_remarks && <div><p className="text-xs font-bold text-[#B0B5C0] uppercase mb-1">Remarks</p><p className="text-sm text-[#4B5563] bg-[#F7F8FA] rounded-xl p-3 leading-relaxed whitespace-pre-wrap">{s.mentor_remarks}</p></div>}
              </div>
            )}
          </div>

          {/* Right Column */}
          <div className="space-y-5">
            {/* Timeline */}
            <div className="ce-card p-5" data-testid="session-timeline">
              <h2 className="text-sm font-bold text-[#1F2937] font-['Outfit'] mb-4">Timeline</h2>
              <div>{steps.map((step, i) => <TimelineStep key={i} label={step.label} done={step.done} active={i === activeIdx} last={i === steps.length - 1} />)}</div>
              {s.completed_at && <p className="text-[10px] text-[#B0B5C0] mt-3">Completed {formatDate(s.completed_at)}</p>}
              {s.certificates_generated_at && <p className="text-[10px] text-[#B0B5C0]">Certificates {formatDate(s.certificates_generated_at)}</p>}
            </div>

            {/* Actions */}
            <div className="space-y-2">
              {canComplete && (
                <Button onClick={() => setShowComplete(true)} className="ce-btn-primary bg-teal-500 text-white hover:bg-teal-600 rounded-xl h-10 w-full font-semibold text-sm" data-testid="complete-session-btn">
                  <CheckCircle2 className="h-4 w-4 mr-2" />Complete Session
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Complete Session Dialog */}
        <Dialog open={showComplete} onOpenChange={setShowComplete}>
          <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto" data-testid="complete-session-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Complete Session</DialogTitle>
              <DialogDescription>Mark attendance and add session notes</DialogDescription>
            </DialogHeader>

            {/* Attendance */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-[#1F2937]">Attendance</Label>
              {(s.students || []).filter(st => ['confirmed', 'pending_parent_confirmation'].includes(st.status) || st.status === 'awaiting_feedback').map((st) => (
                <div key={st.booking_id} className="flex items-center gap-3 bg-[#F7F8FA] rounded-xl p-3" data-testid={`attendance-${st.booking_id}`}>
                  <Switch
                    checked={attendance[st.booking_id]?.attended ?? true}
                    onCheckedChange={(v) => setAttendance(prev => ({ ...prev, [st.booking_id]: { ...prev[st.booking_id], attended: v } }))}
                    data-testid={`attend-toggle-${st.booking_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-[#1F2937]">{st.child_name}</p>
                    <p className="text-[10px] text-[#B0B5C0]">Grade {st.child_grade}</p>
                  </div>
                  <Badge className={`text-[10px] border-0 ${attendance[st.booking_id]?.attended !== false ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {attendance[st.booking_id]?.attended !== false ? 'Present' : 'Absent'}
                  </Badge>
                </div>
              ))}
            </div>

            {/* Session Notes */}
            <div className="space-y-3 mt-4">
              <div>
                <Label className="text-sm font-medium">Session Notes</Label>
                <Textarea value={cNotes} onChange={(e) => setCNotes(e.target.value)} placeholder="What did you cover?" rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="complete-notes" />
              </div>
              <div>
                <Label className="text-sm font-medium">Key Learnings</Label>
                <Textarea value={cLearnings} onChange={(e) => setCLearnings(e.target.value)} placeholder="Key takeaways for students..." rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="complete-learnings" />
              </div>
              <div>
                <Label className="text-sm font-medium">Remarks</Label>
                <Textarea value={cRemarks} onChange={(e) => setCRemarks(e.target.value)} placeholder="Any additional remarks..." rows={2} className="mt-1 rounded-xl border-gray-200" data-testid="complete-remarks" />
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowComplete(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleComplete} disabled={completing} className="bg-teal-500 text-white hover:bg-teal-600 rounded-xl" data-testid="submit-complete-btn">
                {completing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
                Complete Session
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Reissue a single student's certificate — coordinators only */}
        <Dialog open={!!reissue} onOpenChange={() => setReissue(null)}>
          <DialogContent className="rounded-2xl sm:max-w-sm w-[calc(100%-2rem)]" data-testid="group-reissue-dialog">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Reissue this certificate?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[#6B7280] leading-relaxed">
              {reissue?.child_name}&apos;s certificate will be produced again from the current record.
              Use this only to correct the printed details — the certificate number stays the same and
              the old file is replaced.
            </p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setReissue(null)} className="rounded-xl border-gray-200" data-testid="group-reissue-cancel">Cancel</Button>
              <Button onClick={doReissue} disabled={reissuing} className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold" data-testid="group-reissue-confirm">
                {reissuing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reissue certificate'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
